import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Scale, Save, CheckCircle2, Trash2, FileDown } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrackingLayout, RequestError } from "@/components/tracking/TrackingLayout";
import { ImcResult, ImcEvolution, ImcReference } from "@/components/tracking/ImcSections";
import { useRecords } from "@/hooks/useRecords";
import { chartRecords, dateLocale, classifyImc } from "@/lib/tracking";
import { exportImcReport } from "@/lib/reportPdf";

export default function ImcCalculator() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = dateLocale(i18n.language);
  const { records: history, setRecords, error, setError, loading } = useRecords(base44.entities.ImcRecord);
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const chartData = useMemo(() => chartRecords(history, locale, "dd/MM/yy"), [history, locale]);
  const calculate = () => {
    const h = Number(height), w = Number(weight);
    if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0) return;
    const imc = Math.round(w / (h / 100) ** 2 * 10) / 10;
    if (!Number.isFinite(imc)) return;
    setResult({ imc, classification: classifyImc(imc), weight: w, height: h }); setSaved(false);
  };
  const perform = async (action) => {
    setBusy(true); setError("");
    try { await action(); } catch (failure) { setError(failure.message); } finally { setBusy(false); }
  };
  const save = () => perform(async () => {
    const record = await base44.entities.ImcRecord.create({ weight: result.weight, height: result.height, imc: result.imc, classification: t(`imc.classifications.${result.classification.key}`) });
    setRecords((previous) => [record, ...previous]); setSaved(true);
  });
  const clear = () => {
    if (!window.confirm(t("imc.clearAllConfirm"))) return;
    perform(async () => { await Promise.all(history.map((record) => base44.entities.ImcRecord.delete(record.id))); setRecords([]); setResult(null); setSaved(false); });
  };
  return <TrackingLayout title={t("imc.title")} icon={Scale} testId="imc">
    <RequestError error={error} testId="imc-error" />
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <h2 className="font-semibold">{t("imc.calculate")}</h2>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label htmlFor="imc-height">{t("imc.height")}</Label><Input id="imc-height" data-testid="imc-height-input" type="number" min="1" value={height} placeholder={t("imc.heightPlaceholder")} onChange={(event) => setHeight(event.target.value)} className="bg-secondary border-border" /></div>
        <div className="space-y-1.5"><Label htmlFor="imc-weight">{t("imc.weight")}</Label><Input id="imc-weight" data-testid="imc-weight-input" type="number" min="1" step="0.1" value={weight} placeholder={t("imc.weightPlaceholder")} onChange={(event) => setWeight(event.target.value)} className="bg-secondary border-border" /></div>
      </div>
      <Button onClick={calculate} disabled={busy} data-testid="imc-calculate" className="w-full">{t("imc.calculateBtn")}</Button>
    </div>
    {loading && <p data-testid="imc-loading">{t("common.loading")}</p>}
    {result && <ImcResult result={result} />}
    {history.length > 0 && <ImcEvolution history={history} chartData={chartData} />}
    <ImcReference />
    <div className="space-y-3 pb-6">
      {result && !saved && <Button onClick={save} disabled={busy || loading} data-testid="imc-save" className="w-full h-12"><Save className="w-4 h-4 mr-2" />{busy ? t("common.saving") : t("imc.saveRecord")}</Button>}
      {result && saved && <div data-testid="imc-saved" className="flex items-center justify-center gap-2 text-green-400 py-3"><CheckCircle2 className="w-4 h-4" />{t("imc.saved")}</div>}
      <Button onClick={() => perform(() => exportImcReport({ result, history, t, locale }))} disabled={busy} data-testid="imc-export-pdf" variant="outline" className="w-full h-12 text-primary"><FileDown className="w-4 h-4 mr-2" />{t("common.downloadPdf")}</Button>
      <Button onClick={clear} disabled={busy || !history.length} data-testid="imc-delete-history" variant="outline" className="w-full h-12 text-red-400"><Trash2 className="w-4 h-4 mr-2" />{t("imc.clearAll")}</Button>
      <Button onClick={() => navigate(-1)} data-testid="imc-return" variant="outline" className="w-full h-12"><ArrowLeft className="w-4 h-4 mr-2" />{t("common.back")}</Button>
    </div>
  </TrackingLayout>;
}